import {Pill, PillSize} from '@hm/fundamental'
import {Box} from '@mui/material'
import {TFunction} from 'i18next'

import {type ChipsFilterOption} from '../../types'
import {getTranslationKey} from '../../utils'

type ChipsFilterProps<TOptionValue extends string> = {
  options: ChipsFilterOption<TOptionValue>[]
  selectedOptions: TOptionValue[]
  onChange: (selected: TOptionValue[]) => void
  t: TFunction
  translationPrefix?: string
  customAllLabel?: string
  pillSize?: PillSize
}

export const ChipsFilter = <TOptionValue extends string>({
  options,
  selectedOptions,
  onChange,
  t,
  translationPrefix,
  customAllLabel,
  pillSize = 'regular'
}: ChipsFilterProps<TOptionValue>) => {
  const handleChipClick = (optionId: TOptionValue) => {
    const isSelected = selectedOptions.includes(optionId)
    const newSelectedOptions = isSelected
      ? selectedOptions.filter((id) => id !== optionId)
      : [...selectedOptions, optionId]

    onChange(newSelectedOptions)
  }
  const handleAllClick = () => onChange([])

  const isAllSelected = selectedOptions.length === 0

  return (
    <Box
      sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, cursor: 'default'}}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <Pill
        key="all"
        text={customAllLabel ?? t(getTranslationKey('chipsFilter.all', translationPrefix))}
        type={isAllSelected ? 'primary' : 'neutral'}
        variant={isAllSelected ? 'solid' : 'shade'}
        onClick={handleAllClick}
        size={pillSize}
      />
      {options.map((option) => (
        <Pill
          key={option.value}
          text={option.label}
          type={selectedOptions.includes(option.value) ? 'primary' : 'neutral'}
          variant={selectedOptions.includes(option.value) ? 'solid' : 'shade'}
          onClick={() => handleChipClick(option.value)}
          size={pillSize}
        />
      ))}
    </Box>
  )
}
